import { Suspense, useEffect, useState } from "react";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import { getBillingManagement } from "../../../common/services/billing-management";
import { IBillingManagement } from "../../../types/billing-management.type";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { PAYMENT_STATUS } from "../../../types/payment.type";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import TableFooter from "../../../common/components/ui/table/TableFooter";

const STATUS_LABELS = {
  [PAYMENT_STATUS.PENDING]: "Pendente",
  [PAYMENT_STATUS.RECEIVED]: "Recebido",
  [PAYMENT_STATUS.CONFIRMED]: "Confirmado",
  [PAYMENT_STATUS.OVERDUE]: "Atrasado",
};

// eslint-disable-next-line react-refresh/only-export-components
export const SITUATION_SPLIT = {
  false: "Incorreto",
  true: "Correto",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getStatusLabels(status: string) {
  return STATUS_LABELS[status] || status;
}

export default function TableBillingManagement() {
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [payments, setPayments] = useState<IBillingManagement[]>([]);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    splitDone: undefined,
    orderBy: "",
  });

  useEffect(() => {
    async function loadPaymentsManagement(filters) {
      setIsLoading(true);
      setPayments([]);
      try {
        const result = await getBillingManagement({
          ...filters,
          page,
        });
        if (result.ok) {
          setPayments(result.data);
        } else {
          setPayments([]);
          notifyError("Erro ao carregar os pagamentos de cobrança.");
        }
        setData({
          itemsPerPage: data.itemsPerPage,
          totalItems: result.totalItems,
        });
      } catch (error) {
        console.error("Error loading payments management:", error);
        setPayments([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadPaymentsManagement(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  // Função para aplicar estilo baseado no splitDone
  const formatStyle = (
    content: React.ReactNode,
    data: IBillingManagement
  ): JSX.Element => {
    if (data.splitDone) {
      return <span className="font-semibold">{content}</span>;
    } else {
      // Aplica animação de pulse em vermelho quando splitDone é false
      return (
        <span className="animate-pulse-text font-semibold text-red-600">
          {content}
        </span>
      );
    }
  };

  const renderSplitDoneIcon = (splitDone: boolean): JSX.Element => {
    return (
      <div className="flex justify-center items-center">
        {splitDone ? (
          <FaCheckCircle size={24} className="text-greenPrimary" />
        ) : (
          <FaTimesCircle size={24} className="text-redPrimary animate-pulse" />
        )}
      </div>
    );
  };

  const headers = {
    partnerCompany: "Parceiro",
    licensePlate: "Placa",
    invoiceNumber: "Número da Fatura",
    splitDone: "Check dos Split",
    status: "Status",
    description: "Descrição",
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Gestão de Faturas"
          placeholder="Digite o cliente ou a placa"
          statusOptions={STATUS_LABELS}
          situationOptions={SITUATION_SPLIT}
          onSearch={handleFilterChange}
          entity="billing-management"
          tableData={payments as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={payments as unknown as Record<string, unknown>[]}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={{
            partnerCompany: (partnerCompany, paymentsData) => {
              const payment = paymentsData as IBillingManagement;
              return formatStyle(
                <a
                  href={`/parceiros/${payment.partnerId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/parceiros/${payment.partnerId}`);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-bluePrimary"
                >
                  {partnerCompany as string}
                </a>,
                payment
              );
            },
            licensePlate: (licensePlate, paymentsData) => {
              const payment = paymentsData as IBillingManagement;
              return formatStyle(
                <a
                  href={`/veiculos/${payment.licensePlate}`}
                  onClick={(e) => {
                    e.preventDefault(); // Para evitar o comportamento padrão do link
                    navigate(`/veiculos/${payment.licensePlate}`); // Navegação na mesma página
                  }}
                  target="_blank" // Abre em uma nova aba
                  rel="noopener noreferrer" // Melhoria de segurança
                  className="hover:underline hover:text-bluePrimary"
                >
                  {licensePlate as string}
                </a>,
                payment
              );
            },

            // adicionar https://www.asaas.com/payment/show/
            invoiceNumber: (invoiceNumber, paymentsData) => {
              const payment = paymentsData as IBillingManagement;
              return formatStyle(
                <a
                  href={`https://asaas.com/payment/show/${payment.invoiceNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-bluePrimary"
                >
                  {invoiceNumber as string}
                </a>,
                payment
              );
            },

            splitDone: (splitDone, paymentsData) => {
              const payment = paymentsData as IBillingManagement;
              return formatStyle(
                <a
                  href={`/financeiro/gestao-fatura/${payment.paymentId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/financeiro/gestao-fatura/${payment.paymentId}`, {
                      state: { payment },
                    });
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                  title="Ver detalhes da fatura"
                >
                  {renderSplitDoneIcon(Boolean(splitDone))}
                </a>,
                payment
              );
            },

            status: (paymentStatus, paymentsData) => {
              return formatStyle(
                <a
                  href={`/financeiro/gestao-fatura/${
                    (paymentsData as IBillingManagement).paymentId
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    const payment = paymentsData as IBillingManagement;
                    navigate(`/financeiro/gestao-fatura/${payment.paymentId}`, {
                      state: { payment },
                    });
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-bluePrimary"
                >
                  {getStatusLabels(paymentStatus as string) || ""}
                </a>,
                paymentsData as IBillingManagement
              );
            },

            description: (description, paymentsData) => {
              const payment = paymentsData as IBillingManagement;
              return formatStyle(
                <a
                  href={`/financeiro/gestao-fatura/${payment.paymentId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/financeiro/gestao-fatura/${payment.paymentId}`, {
                      state: { payment },
                    });
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-bluePrimary"
                >
                  {description as string}
                </a>,
                paymentsData as IBillingManagement
              );
            },
          }}
          entity="billing-management"
        />
        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}
